import { createContext, useContext } from 'react';

/**
 * Auth to attach to the agent socket handshake. Exactly one field is typically
 * set. Mirrors what the crm-api socket middleware reads from
 * `socket.handshake.auth`:
 *   - `bearerToken` → `auth.token` (a logged-in user's JWT)
 *   - `apiKey`      → `auth.key`   (a private `wkd_` key OR a public `wkda_` key)
 */
export interface AgentSocketAuth {
	bearerToken?: string;
	apiKey?: string;
}

/**
 * Everything the agent widget needs from its HOST to reach the server, with no
 * dependency on crm-web / core singletons (no AuthService, no http interceptor).
 *
 * crm-web supplies an adapter that wraps its existing ApiService/AuthService;
 * the embeddable widget supplies one backed by a public agent key. This is the
 * seam that lets the same inner widget run inside the CRM and on a third-party
 * website without dragging the CRM's auth internals into the public bundle.
 */
export interface AgentTransport {
	/**
	 * crm-api base URL. May include a trailing `/api` (it is stripped before the
	 * socket namespace is appended) — e.g. `https://api.workd.com/api`.
	 */
	baseUrl: string;
	/** Resolve the auth for the socket handshake at connect time, or null. */
	getAuth(): AgentSocketAuth | null;
	/**
	 * Optional: re-mint the socket auth after the server rejects the handshake
	 * (e.g. an expired JWT). The host owns this — crm-web wires it to
	 * AuthService.refreshAccessToken(); the public embeddable widget OMITS it
	 * because a `wkda_` key is static and has nothing to refresh. When absent,
	 * the manager fails fast on an auth error instead of looping. Returns the
	 * fresh auth to retry with, or null if refresh was not possible.
	 */
	refreshAuth?: () => Promise<AgentSocketAuth | null>;
	/**
	 * Surface internal/developer-only affordances (e.g. the raw tool-call "dev
	 * view"). Hosts default this to false; crm-web sets it for @workd staff.
	 */
	showDeveloperTools?: boolean;
}

const AgentTransportContext = createContext<AgentTransport | null>(null);

/**
 * Provides the host transport to the agent widget subtree. Mount this above
 * `<AiAgent />` (crm-web does it at the app root; the embed does it in its
 * shadow-root mount).
 */
export const AgentTransportProvider = AgentTransportContext.Provider;

/** Read the host-provided transport. Throws if no provider is mounted. */
export function useAgentTransport(): AgentTransport {
	const transport = useContext(AgentTransportContext);
	if (transport == null) {
		throw new Error(
			'useAgentTransport: no AgentTransportProvider found — wrap the agent widget in <AgentTransportProvider value={...}>.',
		);
	}
	return transport;
}

/** Non-throwing variant, for presentational components that can tolerate absence. */
export function useOptionalAgentTransport(): AgentTransport | null {
	return useContext(AgentTransportContext);
}
